/**
 * Security Configuration
 * Centralizes all security-related settings
 *
 * This is the single source of truth for CSP directives.
 * CSPMetaTag.js reads from this config to build the actual meta tag.
 */

const isProduction = process.env.REACT_APP_ENV === 'production';

export const securityConfig = {
  // Content Security Policy
  csp: {
    directives: {
      'default-src': ["'self'"],

      'script-src': [
        "'self'",
        // nonce added dynamically by CSPMetaTag.js at runtime
        "'unsafe-inline'",           // Required for GTM inline initialization
        'https://www.googletagmanager.com',
        'https://*.google-analytics.com',
        // Marketing pixels: The Trade Desk, Meta, and X.
        'https://js.adsrvr.org',
        'https://connect.facebook.net',
        'https://static.ads-twitter.com',
        'https://www.google.com/recaptcha/',
        'https://www.gstatic.com/recaptcha/',
        'https://maps.googleapis.com',
        // Dev only: React DevTools and HMR require unsafe-eval
        ...(!isProduction ? ["'unsafe-eval'"] : []),
      ],

      'script-src-elem': [
        "'self'",
        // nonce added dynamically by CSPMetaTag.js at runtime
        'https://www.googletagmanager.com',
        'https://js.adsrvr.org',
        'https://connect.facebook.net',
        'https://static.ads-twitter.com',
        'https://www.google.com/recaptcha/',
        'https://www.gstatic.com/recaptcha/',
        'https://maps.googleapis.com',
      ],

      'style-src': [
        "'self'",
        "'unsafe-inline'",           // Required for Tailwind inline styles and React components
        'https://fonts.googleapis.com',
        'https://fonts.gstatic.com',
      ],

      'style-src-elem': [
        "'self'",
        "'unsafe-inline'",
        'https://fonts.googleapis.com',
        'https://fonts.gstatic.com',
      ],

      'style-src-attr': ["'unsafe-inline'"],

      'img-src': [
        "'self'",
        'data:',
        'blob:',
        'https:',                    // Broad HTTPS images (CDN, merchant images)
        'https://*.googleapis.com',  // Google Maps tiles
        'https://*.gstatic.com',     // Google Maps assets
        'https://*.pulseid.com',     // PulseId CDNs
      ],

      'font-src': [
        "'self'",
        'https://fonts.gstatic.com',
        'data:',
      ],

      'connect-src': [
        "'self'",
        // apiBaseUrl added dynamically by CSPMetaTag.js at runtime
        'https://www.googletagmanager.com',
        'https://*.google-analytics.com',
        'https://*.analytics.google.com',
        'https://*.googletagmanager.com',
        'https://www.googleadservices.com',
        'https://googleads.g.doubleclick.net',
        'https://pagead2.googlesyndication.com',
        'https://match.adsrvr.org',
        'https://insight.adsrvr.org',
        'https://www.facebook.com',
        'https://connect.facebook.net',
        'https://mpc2-prod-29-is5gn1632q-uc.a.run.app',
        'https://demo-1.conversionsapigateway.com',
        'https://analytics.twitter.com',
        'https://t.co',
        'https://maps.googleapis.com',
        'https://*.googleapis.com',  // Other Google APIs
        'https://www.google.com',    // reCAPTCHA API calls
        'https://voxprod-catalyst-client-statics.s3.ap-southeast-1.amazonaws.com',
        'https://*.pulseid.com',     // PulseId CDNs
        'https://*.sentry.io',       // Sentry error reporting (if used)
        // Dev only: webpack HMR websocket
        ...(!isProduction ? ['ws:', 'wss:'] : []),
      ],

      'frame-src': [
        'https://www.googletagmanager.com',
        'https://insight.adsrvr.org',
        'https://www.google.com/recaptcha/',
        'https://recaptcha.google.com/recaptcha/',
      ],

      'object-src': ["'none'"],

      'media-src': ["'self'", 'data:'],

      'base-uri': ["'self'"],

      'form-action': ["'self'"],

      // upgrade-insecure-requests: production only
      // frame-ancestors: only works via HTTP header, not meta tag — set in nginx
    },

    // Enable nonce generation
    useNonce: true,

    // Optional: CSP violation report endpoint
    reportUri: process.env.REACT_APP_CSP_REPORT_URI || null,
  },

  // CORS Configuration
  cors: {
    allowedOrigins: [
      process.env.REACT_APP_API_BASE_URL,
    ],
    allowCredentials: true,
  },

  // Session Management
  session: {
    timeout: parseInt(process.env.REACT_APP_SESSION_TIMEOUT) || 1800000, // 30 minutes
    tokenRefreshInterval: parseInt(process.env.REACT_APP_TOKEN_REFRESH_INTERVAL) || 300000, // 5 minutes
    cookieOptions: {
      secure: isProduction,
      sameSite: 'strict',
      httpOnly: false, // Client-side needs access
      path: '/',
    },
  },

  // Rate Limiting
  rateLimit: {
    maxLoginAttempts: parseInt(process.env.REACT_APP_MAX_LOGIN_ATTEMPTS) || 5,
    lockoutDuration: parseInt(process.env.REACT_APP_LOCKOUT_DURATION) || 900000, // 15 minutes
    maxApiCallsPerMinute: 60,
  },

  // Input Validation
  validation: {
    maxInputLength: 1000,
    maxFileSize: 5242880, // 5MB
    allowedFileTypes: ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'],
  },

  // Encryption
  encryption: {
    algorithm: 'AES',
    keySize: 256,
    iterations: 10000,
  },

  // Security Headers (set via nginx in production — kept here as reference)
  securityHeaders: {
    'X-Content-Type-Options': 'nosniff',
    'X-Frame-Options': 'DENY',
    'X-XSS-Protection': '1; mode=block',
    'Referrer-Policy': 'strict-origin-when-cross-origin',
    'Permissions-Policy': 'geolocation=(), microphone=(), camera=()',
  },

  // Password Policy
  password: {
    minLength: 12,
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: true,
    preventCommon: true,
  },
};

export default securityConfig;
